import React, { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { PesticideActions } from '@galo-monitor/stores/pesticide'
import DataTable from '@galo-monitor/web-components/DataTable'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import PesticideModal from '@galo-monitor/web-containers/modals/root/PesticideModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const Pesticides = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const { data: pesticides, mutate, isValidating } = useGaloMonitorApi('/admin/defensives')

  const userActions = useMemo(
    () => getActionsBySlug(routes.pesticides.slug),
    [getActionsBySlug, routes.pesticides.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'name'
      },
      {
        title: t('pesticide type'),
        field: 'defensiveType'
      },
      {
        title: t('origin'),
        field: 'origin'
      },
      {
        title: t('formulation'),
        field: 'formulation'
      },
      {
        title: t('manufacturer', { howMany: 1 }),
        field: 'defensivesMaker'
      }
    ],
    [t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-pesticide',
        Component: PesticideModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, pesticide) => {
      createDialog({
        id: 'edit-pesticide',
        Component: PesticideModal,
        props: {
          pesticide,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onDeleteClick = useCallback(
    (_, mod) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: toLower(t('pesticide', { howMany: 1 })),
            name: mod.name
          }),
          onConfirm: () => {
            dispatch(PesticideActions.removePesticide(
              mod,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('pesticide', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'male', howMany: 1 }))
                }))
                mutate()
              },
              ({ response }) => {
                createDialog({
                  id: 'pesticide-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'male',
                      action: toLower(t('remove')),
                      this: toLower(t('pesticide', { howMany: 1 }))
                    })  }.`,
                    message: `${ t(response.data.message) }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  return (
    <DataTable
      loading={ isValidating }
      data={ pesticides || [] }
      columns={ columns }
      onAddClick={ userActions.write && onAddClick }
      onEditClick={ userActions.edit && onEditClick }
      onDeleteClick={ userActions.delete && onDeleteClick }
    />
  )
}

export default Pesticides
